import { useAuth } from "../contexts/useAuth";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Activity, MessageSquare, Clock, Cpu, BarChart3, History } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "../components/Modal";
import { useTranslation } from "../contexts/TranslationContext";

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

function Dashboard() {
  const { token, isPaid, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkConfirm, setBulkConfirm] = useState(false);

  const fetchSessions = useCallback(async (p) => {
    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/api/interviews?paginated=1&page=${p}&limit=${pageSize}`,
        { headers: { "Authorization": `Bearer ${token}` } }
      );
      if (res.status === 401 || res.status === 403) { logout(); navigate("/"); return; }
      if (!res.ok) { setError(true); return; }
      {
        const data = await res.json();
        setSessions(data.items || []);
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 1);
        setSelectedIds([]);
      }
    } catch (err) { console.error(err); }
  }, [token, pageSize, logout, navigate]);

  useEffect(() => {
    if (!token || !isPaid) { navigate("/"); return; }

    const fetchAnalytics = async () => {
      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/analytics/summary`, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        // Sessao expirada / nao autorizada: desloga em vez de cair no estado "carregado vazio"
        if (res.status === 401 || res.status === 403) { logout(); navigate("/"); return; }
        if (!res.ok) { setError(true); return; }
        setAnalytics(await res.json());
      } catch (err) { console.error(err); setError(true); }
      finally { setLoading(false); }
    };
    fetchAnalytics();
  }, [token, isPaid, navigate, logout]);

  useEffect(() => {
    if (!token || !isPaid) return;
    fetchSessions(page);
  }, [page, token, isPaid, fetchSessions]);

  const openSession = async (id) => {
    setSelectedSession(id);
    setLoadingDetail(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/interviews/${id}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) setSessionDetail(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoadingDetail(false); }
  };

  const confirmDelete = async () => {
    if (!sessionToDelete) return;
    const id = sessionToDelete._id;
    setDeleting(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/interviews/${id}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        if (selectedSession === id) { setSelectedSession(null); setSessionDetail(null); }
        toast.success(t("dashboard.toastDeleted"));
        if (sessions.length === 1 && page > 1) {
          setPage((p) => p - 1);
        } else {
          fetchSessions(page);
        }
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.error || t("dashboard.toastDeleteError"));
      }
    } catch (err) {
      console.error(err);
      toast.error(t("dashboard.toastDeleteError"));
    } finally {
      setDeleting(false);
      setSessionToDelete(null);
    }
  };

  const changePageSize = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const allSelected = sessions.length > 0 && selectedIds.length === sessions.length;
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : sessions.map((s) => s._id));
  };

  const confirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    const ids = selectedIds;
    setDeleting(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/interviews/bulk-delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ ids }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.success(t("dashboard.toastBulkDeleted", { n: data.deletedCount ?? ids.length }));
        if (selectedSession && ids.includes(selectedSession)) { setSelectedSession(null); setSessionDetail(null); }
        if (ids.length >= sessions.length && page > 1) {
          setPage((p) => p - 1);
        } else {
          fetchSessions(page);
        }
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.error || t("dashboard.toastBulkDeleteError"));
      }
    } catch (err) {
      console.error(err);
      toast.error(t("dashboard.toastBulkDeleteError"));
    } finally {
      setDeleting(false);
      setBulkConfirm(false);
    }
  };

  if (loading) {
    return <div className="flex-1 flex items-center justify-center"><p className="text-gray-400">{t("dashboard.loading")}</p></div>;
  }

  if (error) {
    return <div className="flex-1 flex items-center justify-center"><p className="text-red-400">Erro ao carregar o dashboard.</p></div>;
  }

  return (
    <div className="flex-1 bg-[#0a0a0a] text-white px-6 pt-12 pb-6">
      <div className="max-w-5xl mx-auto w-full">
        <h1 className="text-2xl font-bold text-white mb-6">Dashboard</h1>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { icon: Activity, value: analytics.totalEvents, label: t("dashboard.stats.interactions") },
            { icon: MessageSquare, value: total, label: t("dashboard.stats.sessions") },
            { icon: Clock, value: analytics.responseTime?.avg ? `${Math.round(analytics.responseTime.avg)}ms` : "-", label: t("dashboard.stats.avgTime") },
            { icon: Cpu, value: analytics.modelUsage?.length || 0, label: t("dashboard.stats.modelsUsed") },
          ].map((stat, i) => (
            <div
              key={i}
              className="rounded-xl border border-white/10 bg-white/[0.03] p-5 transition-colors hover:border-brand-400/40"
            >
              <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-brand-500/10 text-brand-400 mx-auto mb-3">
                <stat.icon size={18} />
              </div>
              <p className="text-3xl font-bold text-white leading-none text-center">{stat.value}</p>
              <p className="text-[0.7rem] tracking-[0.08em] uppercase text-gray-400 mt-2 text-center">{stat.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Model Usage */}
      {analytics?.modelUsage?.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 mb-8">
          <h2 className="flex items-center gap-2 text-base font-semibold text-white mb-4">
            <BarChart3 size={18} className="text-brand-400" />
            {t("dashboard.modelUsageTitle")}
          </h2>
          <div className="space-y-2">
            {analytics.modelUsage.map((m, i) => (
              <div key={i} className="flex items-center justify-between text-sm py-2.5 border-b border-white/5 last:border-0">
                <span className="text-gray-300 font-mono">{m._id || 'unknown'}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2.5 py-1 rounded-full bg-white/5 text-gray-300">
                    {t("dashboard.uses", { count: m.count })}
                  </span>
                  <span className="text-xs px-2.5 py-1 rounded-full bg-brand-500/10 text-brand-400 font-medium">
                    {t("dashboard.creditsLabel", { count: m.totalCredits || 0 })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Interview Sessions */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <h2 className="flex items-center gap-2 text-base font-semibold text-white">
            <History size={18} className="text-brand-400" />
            {t("dashboard.historyTitle")} ({total})
          </h2>
          <div className="flex items-center gap-3">
            {selectedIds.length > 0 && (
              <button
                onClick={() => setBulkConfirm(true)}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition"
              >
                <Trash2 size={14} /> {t("dashboard.delete")} ({selectedIds.length})
              </button>
            )}
            <label className="flex items-center gap-1.5 text-xs text-gray-400">
              {t("dashboard.perPage")}
              <select
                value={pageSize}
                onChange={changePageSize}
                className="bg-[#1a1a1a] border border-white/10 rounded-lg px-2 py-1 text-gray-200 outline-none focus:border-brand-500/50"
              >
                {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          </div>
        </div>
        {sessions.length === 0 ? (
          <p className="text-gray-500 text-sm">{t("dashboard.noSessions")}</p>
        ) : (
          <div className="rounded-xl border border-white/10 overflow-hidden divide-y divide-white/5">
            <label className="flex items-center gap-3 px-3 py-2.5 text-xs text-gray-400 cursor-pointer select-none bg-white/[0.02] hover:bg-white/[0.04] transition">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleSelectAll}
                className="accent-brand-500"
              />
              {t("dashboard.selectAll")}
            </label>
            {sessions.map((s) => (
              <div
                key={s._id}
                onClick={() => openSession(s._id)}
                className="flex items-center justify-between py-3 px-3 cursor-pointer hover:bg-white/5 transition"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(s._id)}
                    onChange={() => toggleSelect(s._id)}
                    onClick={(e) => e.stopPropagation()}
                    className="accent-brand-500 shrink-0"
                  />
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium truncate">{s.title}</p>
                    <p className="text-gray-500 text-xs">
                      {s.company && `${s.company} · `}
                      {s.totalQuestions} {t("dashboard.questions")}
                      {s.duration && ` · ${Math.round(s.duration / 60)}min`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    s.status === 'completed' ? 'bg-green-500/10 text-green-400' : 'bg-brand-500/10 text-brand-400'
                  }`}>
                    {s.status === 'completed' ? t("dashboard.statusCompleted") : t("dashboard.statusActive")}
                  </span>
                  <span className="text-gray-500 text-xs">
                    {new Date(s.createdAt).toLocaleDateString('pt-BR')}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); setSessionToDelete(s); }}
                    title={t("dashboard.deleteInterview")}
                    className="p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-white/5">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 rounded-lg text-sm border border-white/10 text-gray-300 hover:bg-white/5 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {t("dashboard.previous")}
            </button>
            <span className="text-gray-400 text-sm">{t("dashboard.pageOf", { page, total: totalPages })}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1.5 rounded-lg text-sm border border-white/10 text-gray-300 hover:bg-white/5 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {t("dashboard.next")}
            </button>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!sessionToDelete}
        onClose={() => { if (!deleting) setSessionToDelete(null); }}
        onConfirm={confirmDelete}
        title={t("dashboard.deleteInterview")}
        message={sessionToDelete ? t("dashboard.deleteConfirmMessage", { title: sessionToDelete.title }) : ""}
        confirmText={deleting ? t("dashboard.deleting") : t("dashboard.delete")}
        cancelText={t("dashboard.cancel")}
        variant="danger"
      />

      <Modal
        isOpen={bulkConfirm}
        onClose={() => { if (!deleting) setBulkConfirm(false); }}
        onConfirm={confirmBulkDelete}
        title={t("dashboard.deleteInterviews")}
        message={t("dashboard.bulkDeleteConfirmMessage", { n: selectedIds.length })}
        confirmText={deleting ? t("dashboard.deleting") : t("dashboard.delete")}
        cancelText={t("dashboard.cancel")}
        variant="danger"
      />

      {/* Session Detail Modal */}
      {selectedSession && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => { setSelectedSession(null); setSessionDetail(null); }}>
          <div className="bg-[#111] border border-white/10 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            {loadingDetail ? (
              <p className="text-gray-400">{t("dashboard.loading")}</p>
            ) : sessionDetail ? (
              <>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-lg font-bold text-brand-500">{sessionDetail.title}</h2>
                    <p className="text-gray-400 text-sm">
                      {sessionDetail.company && `${sessionDetail.company} · `}
                      {sessionDetail.position && `${sessionDetail.position} · `}
                      {new Date(sessionDetail.createdAt).toLocaleDateString('pt-BR')}
                      {sessionDetail.duration && ` · ${Math.round(sessionDetail.duration / 60)}min`}
                    </p>
                  </div>
                  <button onClick={() => { setSelectedSession(null); setSessionDetail(null); }} className="text-gray-500 hover:text-white text-xl">&times;</button>
                </div>

                {sessionDetail.interactions?.length === 0 ? (
                  <p className="text-gray-500 text-sm">{t("dashboard.noInteractions")}</p>
                ) : (
                  <div className="space-y-4">
                    {sessionDetail.interactions?.map((interaction, i) => (
                      <div key={i} className="border-b border-white/5 pb-4 last:border-0">
                        <div className="mb-2">
                          <span className="text-xs text-brand-500 font-semibold">{t("dashboard.question")} {i + 1}</span>
                          {interaction.model && <span className="text-xs text-gray-600 ml-2">({interaction.model})</span>}
                        </div>
                        <p className="text-gray-300 text-sm mb-2 bg-white/5 p-3 rounded">{interaction.question}</p>
                        <p className="text-white text-sm bg-white/5 p-3 rounded whitespace-pre-wrap">{interaction.answer}</p>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p className="text-gray-400">{t("dashboard.errorLoadingSession")}</p>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default Dashboard;
